import { capitalizeFirstLetter } from '../../modules/utils/formatters'
import {
  EMPTY_CHART,
  EMPTY_CHART_WITH_WEIGHTS,
  OPTIONS_TRANSFORM_CONFIG,
  FORMULA_LABEL_PREFIXES
} from './BehaviorSection.constants'

/** Normalizers: map payload items to { name, value, weight } for chart format */
const chartItemNormalizers = {
  dimensions: item => ({
    name: item.dimension?.name ?? '',
    value: item.average ?? 0,
    weight: item.dimension?.weighter ?? 0
  }),
  measurements: item => ({
    name: item.name ?? '',
    value: item.value ?? 0,
    weight: item.weighter ?? 0
  }),
  legacy: item => ({
    name: item.name ?? '',
    value: item.value ?? item.average ?? 0,
    weight: item.weight ?? item.weighter ?? item.dimension?.weighter ?? 0
  })
}

function detectChartPayloadFormat(data) {
  if (!data?.length) return 'legacy'
  const first = data[0]
  if (first.dimension != null && first.average != null) return 'dimensions'
  if (first.indicatorId != null && first.weighter != null) return 'measurements'
  return 'legacy'
}

/**
 * Generic transform: array -> { categories, data, weights? } for charts.
 * @param {Array} data - Backend payload array
 * @param {{ includeWeights?: boolean, format?: 'dimensions'|'measurements'|'legacy' }} options
 */
export const transformToChartFormat = (data, options = {}) => {
  const { includeWeights = false, format } = options
  if (!data || !Array.isArray(data)) {
    return includeWeights ? { ...EMPTY_CHART_WITH_WEIGHTS } : { ...EMPTY_CHART }
  }
  const resolvedFormat = format ?? detectChartPayloadFormat(data)
  const normalizer = chartItemNormalizers[resolvedFormat] ?? chartItemNormalizers.legacy
  const initial = includeWeights ? { categories: [], data: [], weights: [] } : { categories: [], data: [] }
  return data.reduce((acc, curr) => {
    const { name, value, weight } = normalizer(curr)
    acc.categories.push(name)
    acc.data.push(value)
    if (includeWeights) acc.weights.push(weight)
    return acc
  }, initial)
}

/**
 * Transforms backend payload to chart format (dimensions or legacy shape).
 * Supports: [{ dimension: { name, weighter }, average }, ...] or [{ name, value }, ...].
 */
export const transformDataToChart = (data, includeWeights = false) => transformToChartFormat(data, { includeWeights })

/**
 * Transforms getMeasurementsIndicators payload to chart format.
 * Payload shape: [{ indicatorId, name, value, weighter, description, ... }, ...].
 */
export const transformMeasurementsIndicators = (data, includeWeights = true) =>
  transformToChartFormat(data, { includeWeights, format: 'measurements' })

/**
 * Transforms backend payload to select/options format.
 * @param {Array} data - Raw payload array
 * @param {'years'|'months'|'dimensions'|'dimensionsIndicators'} format - Which mapping to use
 * @returns {Array} Array of { name, value } or extended shape for dimensionsIndicators
 */
export const transformToOptions = (data, format) => {
  if (!data || !Array.isArray(data)) return []
  const config = OPTIONS_TRANSFORM_CONFIG[format]
  if (!config) return []
  const sorted = config.sort ? [...data].sort(config.sort) : data
  return sorted.map(config.map)
}

export const transformYearsOptions = data => transformToOptions(data, 'years')
export const transformMonthsOptions = data => transformToOptions(data, 'months')
export const transformDimensionsOptions = data => transformToOptions(data, 'dimensions')
export const transformDimensionsIndicators = data => transformToOptions(data, 'dimensionsIndicators')

/**
 * Returns the value of the last valid month from the given month options.
 * "Last" is the option with the highest numeric value (e.g. February=2 > January=1).
 * Result does not depend on array order.
 * @param {Array<{ name: string, value: number|string }>} options - Month options ({ name, value })
 * @returns {number|string|undefined} Value of the last valid month, or undefined if no options
 */
export const getLastValidMonthValue = options => {
  if (!options?.length) return undefined
  const withValue = options.filter(o => o != null && o.value !== undefined && o.value !== '')
  if (!withValue.length) return undefined
  const last = withValue.reduce((best, curr) => (Number(curr.value) > Number(best.value) ? curr : best))
  return last.value
}

export const setStringMonth = (year, month) => {
  if (!year || !month) return ''

  const monthString = new Date(year, month - 1).toLocaleString('es-CL', { month: 'long' })

  return capitalizeFirstLetter(monthString)
}

export const calculateDefaultYearAndMonth = () => {
  const now = new Date()
  let defaultYear = Number(now.getFullYear())
  const currentMonth1Based = now.getMonth() + 1
  let defaultMonth
  if (currentMonth1Based === 1) {
    defaultYear = defaultYear - 1
    defaultMonth = 12
  } else {
    defaultMonth = currentMonth1Based - 1
  }

  const stringMonth = setStringMonth(defaultYear, defaultMonth)

  return { year: defaultYear, month: defaultMonth, stringMonth }
}

export const calculateSeriesData = (data, includeWeights = false, weights = []) => {
  if (!data || !Array.isArray(data)) return []
  return data.map((value, index) => ({
    y: value,
    weight: includeWeights ? weights?.[index] || 0 : 0
  }))
}

/**
 * Splits the full formula text into prefix (normal weight) and remainder (bold).
 * The backend sends the full text; only the known prefix is rendered without bold.
 * When middleBoldPart is true, only the constant text is bold; prefix and rest are normal.
 * @param {string} fullText - Full text (e.g. "Cantidad de órdenes de compra por Trato Directo")
 * @param {boolean} middleBoldPart - If true, only the matching constant is bold; text before and after stays normal.
 * @returns {{ prefix: string, boldPart: string, rest?: string }}
 */
export const splitFormulaLabel = (fullText, middleBoldPart = false) => {
  if (!fullText || typeof fullText !== 'string') return { prefix: '', boldPart: '', rest: '' }
  const trimmed = fullText.trim()

  for (const constant of FORMULA_LABEL_PREFIXES) {
    const index = trimmed.indexOf(constant)
    if (index === -1) continue

    if (middleBoldPart) {
      return {
        prefix: trimmed.slice(0, index),
        boldPart: constant,
        rest: trimmed.slice(index + constant.length)
      }
    }

    return {
      prefix: constant,
      boldPart: trimmed.slice(constant.length).trim(),
      rest: ''
    }
  }

  return { prefix: trimmed, boldPart: '', rest: '' }
}
